import { uppercase } from '@/globals';
import rootState from '@/store/state';
import i18n from '@/vueI18n';

const FILTERS = 'FILTERS';
const LOCATION_FILTERS = 'LOCATIONFILTERS';

const GEOFENCES_IN_CART = 'geofencesInCart';
const EXISTING_PROXIMITY = 'existingProximity';
const SEARCH_PROXIMITY = 'searchProximity';
const MEDIATYPE_IN_CART = 'includedMediaTypes';

const availableFilters = [
  { name: 'locationFilters', type: LOCATION_FILTERS },
  { name: 'filters', type: FILTERS },
];

// GLOSSARY
// Filters:  filters and location filters
// Targeting: screen, site, market, city, region, country, format, network, venueType, proximity, tag, packs
// Methods: includedXX or excludedXX

export default class TargetingLogic {
  constructor() {
    this.cart = {};
    this.hasGroup = false;
    this.hasORFilter = false;
    this.targetingLogicHTML = '';
    this.listOfElementsPerMethod = [];
    this.logicalExpressionPerFilter = '';
    this.limitOfElementsPerGroup = 5;
  }

  add(cart) {
    this.cart = cart;
    this.convertLogicalFiltersToCommonLanguage();
  }

  convertLogicalFiltersToCommonLanguage() {
    this.hasORFilter = false;
    this.targetingLogicHTML = '';
    this.logicalExpressionPerFilter = '';

    availableFilters.forEach((filter) => {
      const typeOfFilter = TargetingLogic[filter.name];

      // Loop over each type of filter
      Object.keys(typeOfFilter).forEach((targeting) => {
        const method = typeOfFilter[targeting];
        const elementsPerMethod = this.cart[method]?.length;
        if (elementsPerMethod > 0) {
          this.buildBooleanLogic(method, filter, targeting);
        }
      });
      // Update the HTML expression
      this.updateTargetingLogicHTML();
    });
  }

  buildBooleanLogic(method, filter, targeting) {
    this.hasGroup = false;
    this.hasORFilter = false;
    const elementsPerMethod = this.cart[method]?.length;
    const canAddOR = filter.type === LOCATION_FILTERS && this.logicalExpressionPerFilter !== '';
    const canAddAND = !this.hasORFilter && (this.logicalExpressionPerFilter !== '' || this.targetingLogicHTML !== '');

    // OR (only when filter is a location filter)
    if (canAddOR) {
      this.addOR(filter, targeting);
    } else if (canAddAND) {
      // AND
      this.addAND();
    }

    this.logicalExpressionPerFilter += `<span class="boolean-expression" data-filter-boolean-expression-for="${targeting}">`;

    if (this.hasORFilter) {
      this.hasORFilter = false;
    }

    // TARGETING NAME
    this.addTargetingName(targeting, elementsPerMethod > 1);

    // NOT
    if (TargetingLogic.isExcluded(method)) {
      if (elementsPerMethod > 1) {
        // NONE OF: excluded GROUP elements
        this.addNONEOF();
      } else {
        // NOT: excluded single element
        this.addNOT();
      }
    } else if (elementsPerMethod > 1) {
      // ANY OF: included GROUP elements
      this.addANYOF();
    }

    // START GROUP
    if (elementsPerMethod > 1) {
      this.startGroup(elementsPerMethod);
    }

    // LIST
    this.listOfElementsPerMethod = [];
    this.cart[method].forEach((x, index) => {
      const name = ([GEOFENCES_IN_CART, MEDIATYPE_IN_CART].includes(method) && x.displayName) || x.name || x.id || x;
      this.addELEMENT(name);
      if (elementsPerMethod > 1 && elementsPerMethod - 1 !== index) {
        this.addSPACE();
      }
      this.listOfElementsPerMethod.push(name);
    });

    // TRUNCATE
    if (this.listOfElementsPerMethod.length > this.limitOfElementsPerGroup) {
      this.addEllipsis();
      this.buildFullList(this.listOfElementsPerMethod.join(', '));
    }

    // END GROUP
    if (this.hasGroup) {
      this.endGroup();
    }

    this.logicalExpressionPerFilter += '</span>';
  }

  startGroup(elementsPerMethod, isNumber) {
    const isHoverable = elementsPerMethod > this.limitOfElementsPerGroup || isNumber ? 'hoverable' : '';
    if (!isNumber) {
      this.logicalExpressionPerFilter += '<span>(</span>';
      this.hasGroup = true;
    }
    this.logicalExpressionPerFilter += `<div class="group-filter ${isHoverable}">`;
  }

  endGroup(isNumber) {
    this.logicalExpressionPerFilter += '</div>';
    if (!isNumber) {
      this.logicalExpressionPerFilter += '<span>)</span>';
      this.hasGroup = false;
    }
  }

  addOR(filter, name) {
    this.hasORFilter = true;
    this.logicalExpressionPerFilter += ` <span class="operand">${i18n.global.t('conjuctions.or')}</span> `;
  }

  addAND() {
    this.logicalExpressionPerFilter += ` <span class="operand">${i18n.global.t('conjuctions.and')}</span> `;
  }

  addIN() {
    this.logicalExpressionPerFilter += ` <span class="operand">${i18n.global.t('conjuctions.in')}</span> `;
  }

  addNOT() {
    this.logicalExpressionPerFilter += ` <span class="operand">${i18n.global.t('conjuctions.not')}</span> `;
  }

  addNONEOF() {
    this.logicalExpressionPerFilter += ` <span class="operand">${i18n.global.t('conjuctions.noneOf')}</span> `;
  }

  addANYOF() {
    this.logicalExpressionPerFilter += ` <span class="operand">${i18n.global.t('conjuctions.anyOf')}</span> `;
  }

  addSPACE() {
    this.logicalExpressionPerFilter += '<span>,</span>';
  }

  addELEMENT(name) {
    this.logicalExpressionPerFilter += `<span class="rule">${name}</span>`;
  }

  addTargetingName(type, hasMoreThanOneElement) {
    if (TargetingLogic.isExcluded(type)) {
      type = type.split('Exclusion')[0];
    }

    if (type === EXISTING_PROXIMITY || type === SEARCH_PROXIMITY) {
      type = 'proximity';
    }

    const name = uppercase(i18n.global.t(`names.${type}`, hasMoreThanOneElement ? 2 : 1));
    const is = i18n.global.t('names.is', hasMoreThanOneElement ? 2 : 1);
    this.logicalExpressionPerFilter += ` <span class="filter-type">${name}</span> `;
    this.logicalExpressionPerFilter += ` <span>${is}</span> `;
  }

  addEllipsis() {
    this.logicalExpressionPerFilter += ', ...';
  }

  buildFullList(list) {
    this.logicalExpressionPerFilter += `<div class="full-list">${list.length > 1000 ? `${list.slice(0, 1000)}...` : list}</div>`;
  }

  updateTargetingLogicHTML() {
    this.targetingLogicHTML += this.logicalExpressionPerFilter;
    this.logicalExpressionPerFilter = '';
  }

  static isExcluded(filter) {
    return filter.includes('excluded') || filter.includes('Exclusion');
  }

  static setExclusionFilters(filters, exclusion) {
    const updatedFilters = { ...filters };
    exclusion.forEach(({ name, method }) => {
      updatedFilters[`${name}Exclusion`] = method;
    });
    return updatedFilters;
  }

  static get locationFilters() {
    return {
      screen: 'includedUnits',
      site: 'includedSites',
      market: 'includedMarkets',
      city: 'includedCities',
      region: 'includedRegions',
      country: 'includedCountries',
    };
  }

  static get filters() {
    const filters = {
      format: 'includedCreativeFormats',
      network: 'includedNetworks',
      venueType: 'includedMediaTypes',
      existingProximity: 'includedGeofenceGroups',
      searchProximity: 'includedGeofences',
      tag: 'includedTags',
      language: 'includedLanguages',
      orientation: 'includedCreativeOrientations',
      iabCategory: 'includedIabCategories',
    };

    const exclusion = [
      { name: 'screen', method: 'excludedUnits' },
      { name: 'site', method: 'excludedSites' },
      { name: 'market', method: 'excludedMarkets' },
      { name: 'city', method: 'excludedCities' },
      { name: 'region', method: 'excludedRegions' },
      { name: 'country', method: 'excludedCountries' },
      { name: 'format', method: 'excludedCreativeFormats' },
      { name: 'network', method: 'excludedNetworks' },
      { name: 'venueType', method: 'excludedMediaTypes' },
      { name: 'existingProximity', method: 'excludedGeofenceGroups' },
      { name: 'searchProximity', method: 'excludedGeofences' },
      { name: 'tag', method: 'excludedTags' },
      { name: 'language', method: 'excludedLanguages' },
      { name: 'orientation', method: 'excludedCreativeOrientations' },
      { name: 'iabCategory', method: 'excludedIabCategories' },
    ];

    if (rootState.orgApp.organization != null && rootState.orgApp.organization.isSupplyOrganization) {
      filters.unitPack = 'includedUnitPacks';
      exclusion.push({ name: 'unitPack', method: 'excludedUnitPacks' });
    }

    const updatedFilters = TargetingLogic.setExclusionFilters(filters, exclusion);

    return updatedFilters;
  }
}
